import { onboardingApiClient, OnboardingData, OnboardingResponse, OnboardingStatusResponse, CompanyContextResponse, SaveCompanyContextPayload } from '@/clients/onboardingApiClient';

export type { OnboardingData, OnboardingResponse, OnboardingStatusResponse, CompanyContextResponse, SaveCompanyContextPayload };

/** broken-fiscal-year.AC2/EC4: Swedish bokföringslagen's cap on an extended fiscal year — kept in
 * sync with the backend's own MAX_FISCAL_YEAR_MONTHS (fiscalYearService.ts) by convention, since
 * this is a separate runtime; the backend is the enforced source of truth. */
export const MAX_FISCAL_YEAR_MONTHS = 18;

const FISCAL_PERIOD_END: Record<string, [month: number, day: number]> = {
  'Kalenderår (jan–dec)': [12, 31],
  'Apr–mar': [3, 31],
  'Jul–jun': [6, 30],
  'Okt–sep': [9, 30],
};

/**
 * broken-fiscal-year.AC9: the single, named, swappable source of the onboarding form's default
 * first-year start date. Today the real company registration date isn't captured anywhere yet —
 * this is the one function a future Bolagsverket/allabolag lookup replaces, without touching the
 * form, the edit flow, or any validation logic.
 */
export function getDefaultFiscalYearStartDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * broken-fiscal-year.F1: suggests a first-year end date from the chosen normal-cadence period —
 * the first occurrence of that period's year-end on or after `startDate` — while leaving the
 * field freely editable, so the result can be shorter or longer than 12 months by design.
 * Returns '' when either input is missing or the period is unrecognized.
 */
export function suggestFiscalYearEndDate(startDate: string, period: string): string {
  const target = FISCAL_PERIOD_END[period];
  if (!startDate || !target) return '';
  const [month, day] = target;
  const start = new Date(startDate + 'T00:00:00');
  if (isNaN(start.getTime())) return '';
  let candidate = new Date(start.getFullYear(), month - 1, day);
  if (candidate <= start) {
    candidate = new Date(start.getFullYear() + 1, month - 1, day);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${candidate.getFullYear()}-${pad(candidate.getMonth() + 1)}-${pad(candidate.getDate())}`;
}

/** Whole months from `startDate` to `endDate` inclusive, or null if either is missing/invalid. */
export function fiscalYearLengthInMonths(startDate: string, endDate: string): number | null {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return null;
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
}

export const onboardingService = {
  async onboardCompany(data: OnboardingData): Promise<OnboardingResponse> {
    return onboardingApiClient.onboardCompany(data);
  },
  async getStatus(): Promise<OnboardingStatusResponse> {
    return onboardingApiClient.getStatus();
  },
  async updateKycCompletion(completed: boolean): Promise<OnboardingResponse> {
    return onboardingApiClient.updateKycCompletion(completed);
  },
  async getCompanyContext(): Promise<CompanyContextResponse> {
    return onboardingApiClient.getCompanyContext();
  },
  async saveCompanyContext(
    contextText: string,
    fiscalYear: string,
    fiscalYearStartDate: string,
    fiscalYearEndDate: string,
    accountingMethod: string,
    companyType: string,
    vatRegistered: boolean,
    fSkatt: boolean,
  ): Promise<OnboardingResponse> {
    return onboardingApiClient.saveCompanyContext({
      contextText, fiscalYear, fiscalYearStartDate, fiscalYearEndDate, accountingMethod, companyType, vatRegistered, fSkatt,
    });
  },
};
